import React from 'react';
import { Row, Col, FormGroup, Label, Button } from 'reactstrap';
import { Field, FieldArray, ErrorMessage } from 'formik';
import { useTenant } from '../../../../hooks/useTenant';
import { storage } from '../../../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { getRankImage } from '../../../../models/gamification';

const GamificationLevelRanks = ({ values, setFieldValue }) => {
    const [editingIndex, setEditingIndex] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState({});
    const { idTenant } = useTenant();

    const handleImageUpload = async (e, index, rankId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem válida.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            toast.error('A imagem deve ter no máximo 2MB.');
            return;
        }

        setIsUploading(prev => ({ ...prev, [index]: true }));

        try {
            const fileExtension = file.name.split('.').pop();
            const fileName = `${rankId}_${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `tenants/${idTenant}/gamification/levelRanks/${fileName}`);
            
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setFieldValue(`levelRanks.${index}.imageId`, downloadURL);
            toast.success('Imagem atualizada com sucesso!');
        } catch (error) {
            console.error("Erro ao fazer upload:", error);
            toast.error('Erro ao fazer upload da imagem.');
        } finally {
            setIsUploading(prev => ({ ...prev, [index]: false }));
        }
    };



    return (
        <div className="p-3">
            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                <i className="mdi mdi-trending-up me-2 text-primary font-size-16"></i>
                Ranking de Níveis (Level Ranks)
            </h6>
            
            <p className="text-muted small mb-4">
                Configure os nomes e a pontuação mínima (XP) necessária para um aluno atingir cada nível de ranking. As imagens abaixo correspondem à progressão do sistema.
            </p>

            <FieldArray name="levelRanks">
                {({ push, remove }) => (
                    <div>
                        {values.levelRanks && values.levelRanks.length > 0 ? (
                            <Row className="g-4">
                                {values.levelRanks.map((rank, index) => (
                                    <Col xl={3} lg={4} md={6} sm={12} key={rank.id || index}>
                                        {editingIndex === index ? (
                                            <div className="p-3 border-top border-warning text-start h-100 position-relative rounded-0">
                                                {index > 0 && (
                                                    <Button 
                                                        color="danger" 
                                                        size="sm" 
                                                        className="rounded-0 position-absolute"
                                                        style={{ top: '-10px', right: '-10px', width: '28px', height: '28px', padding: 0 }}
                                                        onClick={() => remove(index)}
                                                    >
                                                        <i className="mdi mdi-close"></i>
                                                    </Button>
                                                )}

                                                <FormGroup className="mb-2">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Nome do Nível</Label>
                                                    <Field 
                                                        type="text" 
                                                        className="form-control form-control-sm rounded-0" 
                                                        name={`levelRanks.${index}.name`} 
                                                        placeholder="Ex: Bronze" 
                                                    />
                                                    <ErrorMessage name={`levelRanks.${index}.name`} component="div" className="text-danger mt-1 font-size-12" />
                                                </FormGroup>

                                                <FormGroup className="mb-2">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">XP Mínimo</Label>
                                                    <Field 
                                                        type="number" 
                                                        className="form-control form-control-sm rounded-0" 
                                                        name={`levelRanks.${index}.minXp`} 
                                                        placeholder="0" 
                                                        readOnly={index === 0}
                                                    />
                                                    {index === 0 && (
                                                        <small className="text-muted font-size-10 d-block mt-1">Sempre inicia com 0 XP.</small>
                                                    )}
                                                    <ErrorMessage name={`levelRanks.${index}.minXp`} component="div" className="text-danger mt-1 font-size-12" />
                                                </FormGroup>

                                                <FormGroup className="mb-3">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Upload de Imagem</Label>
                                                    <input 
                                                        type="file" 
                                                        accept="image/*"
                                                        className="form-control form-control-sm rounded-0"
                                                        disabled={isUploading[index]}
                                                        onChange={(e) => handleImageUpload(e, index, rank.id || `rank_${index}`)}
                                                    />
                                                    {isUploading[index] && (
                                                        <small className="text-primary d-block mt-1">
                                                            <i className="mdi mdi-loading mdi-spin me-1"></i>Enviando imagem...
                                                        </small>
                                                    )}
                                                </FormGroup>

                                                <Button color="primary" size="sm" className="w-100 rounded-0" onClick={() => setEditingIndex(null)}>
                                                    OK
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="p-3 border-top text-center h-100 position-relative rounded-0">
                                                <Button 
                                                    color="light" 
                                                    size="sm" 
                                                    className="rounded-0 position-absolute border text-secondary"
                                                    style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '12px' }}
                                                    onClick={() => setEditingIndex(index)}
                                                >
                                                    <i className="mdi mdi-pencil"></i>
                                                </Button>

                                                <div className="position-absolute top-0 start-50 translate-middle badge bg-primary rounded-0 px-2 py-1 font-size-11">
                                                    {rank.minXp} XP
                                                </div>
                                                
                                                <div className="mt-3 mb-2">
                                                    <div 
                                                        className="mx-auto d-flex align-items-center justify-content-center mb-2"
                                                        style={{ width: '72px', height: '72px', overflow: 'hidden' }}
                                                    >
                                                        {getRankImage(rank.imageId || rank.id) ? (
                                                            <img 
                                                                src={getRankImage(rank.imageId || rank.id)} 
                                                                alt={rank.id} 
                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }} 
                                                            />
                                                        ) : (
                                                            <i className="mdi mdi-shield-star text-warning font-size-48"></i>
                                                        )}
                                                    </div>
                                                </div>

                                                <h6 className="fw-bold text-dark mb-1 font-size-13">{rank.name}</h6>
                                            </div>
                                        )}
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                             <div className="text-center p-5 rounded-0 border border-dashed border-secondary mb-4">
                                <i className="mdi mdi-trending-up font-size-40 text-muted mb-3 d-block"></i>
                                <h6 className="text-muted fw-bold">Nenhum nível configurado</h6>
                            </div>
                        )}

                        <div className="text-center mt-4">
                            <Button 
                                type="button" 
                                color="primary" 
                                outline 
                                className="rounded-0 px-4 fw-bold"
                                onClick={() => {
                                    const nextIndex = values.levelRanks ? values.levelRanks.length : 0;
                                    const nextId = nextIndex + 1;
                                    const lastXp = nextIndex > 0 ? Number(values.levelRanks[nextIndex - 1].minXp) || 0 : 0;
                                    const nextXp = lastXp + 100;

                                    push({
                                        id: `rank_${Date.now()}`,
                                        name: `Nível ${nextId}`,
                                        minXp: nextIndex === 0 ? 0 : nextXp,
                                        imageId: `rank_${nextId <= 20 ? nextId : 20}`
                                    });
                                    setEditingIndex(nextIndex);
                                }}
                            >
                                <i className="mdi mdi-plus me-2"></i>
                                Adicionar Novo Nível
                            </Button>
                        </div>
                    </div>
                )}
            </FieldArray>
        </div>
    );
};

export default GamificationLevelRanks;
